import R from 'ramda'
import { useState } from 'react'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import { Box, LoadingButton, useToast, Link } from '@redriverpress/system'

import InputPassword from 'Common/components/forms/InputPassword'

import { useStudentOnboardingContext } from '../../context/onboardingStudentContext'
import Loc from './Loc'
import { Field } from '../utils/FormUtilComponents'
import { signIn } from '../services'

const LOGIN_ERROR_MESSAGES = {
  invalid: 'Your username, email, or password is incorrect.',
  last_attempt: 'You have one more attempt before your account is locked.',
  locked: 'Your account is locked, please contact our support for assistance.',
}

export default function StudentLoginForm() {
  const [{ locale, classCode }] = useStudentOnboardingContext()

  const [loading, setLoading] = useState(false)
  const { addToast } = useToast()

  const schema = Yup.object().shape({
    login: Yup.string().required('Email or username is required'),
    password: Yup.string().required('Password is required'),
  })

  const initialValues = {
    login: '',
    password: '',
    remember_me: false,
  }

  const getErrorMessage = error => {
    // Default to invalid
    return LOGIN_ERROR_MESSAGES[error] || LOGIN_ERROR_MESSAGES.invalid
  }

  const handleSubmit = formValues => {
    setLoading(true)

    const user = R.merge({ class_code: classCode }, formValues)

    signIn({ user })
      .then(res => {
        window.location.href = res.data.after_sign_in_path
          ? res.data.after_sign_in_path
          : '/students/dashboard'
      })
      .catch(res => {
        setLoading(false)
        const error = { ...res }
        if (error.response.status !== 401) {
          addToast({
            id: 'login-error',
            content: 'There was a problem. Please try again.',
            type: 'warning',
            timeout: 5000,
          })
        } else {
          addToast({
            id: 'login-error',
            content: getErrorMessage(error.response.data.error),
            type: 'warning',
            timeout: 5000,
          })
        }
      })
  }

  return (
    <Formik
      initialValues={initialValues}
      initialErrors={initialValues}
      validationSchema={schema}
      onSubmit={handleSubmit}
    >
      {formik => {
        const { errors, touched, isValid } = formik

        const getFieldBorderColor = field => {
          if (!touched[field]) return null
          if (errors[field]) return 'red700'
          return null
        }

        return (
          <Box
            display='flex'
            flexDirection='column'
            alignItems='center'
            width={[1, null, '70%', '50%']}
          >
            <Form
              autoComplete='off'
              style={{
                display: 'flex',
                flex: '1',
                flexDirection: 'column',
                width: '100%',
              }}
            >
              {/* FIELDS */}

              <Box width={1} mb={3}>
                {!!locale && (
                  <Loc
                    fontSize={0}
                    color='gray700'
                    fontWeight='400'
                    copy='Email'
                    textAlign='left'
                  />
                )}
                <Field
                  id='login'
                  name='login'
                  borderColor={getFieldBorderColor('login')}
                  mb={0}
                  icon='address-card'
                  placeholder='Email or username'
                />
              </Box>

              <Box width={1} mb={2}>
                {!!locale && (
                  <Loc
                    fontSize={0}
                    color='gray700'
                    fontWeight='400'
                    copy='Password'
                    textAlign='left'
                  />
                )}
                <Field
                  // Since Field is a styled-component, we must use the forwardedAs prop (not as) to pass the InputPassword component
                  forwardedAs={InputPassword}
                  id='password'
                  name='password'
                  mb={0}
                  borderColor={getFieldBorderColor('password')}
                  icon='key-skeleton'
                  placeholder='Password'
                />
              </Box>

              <Box width={1} mt={2} textAlign='center'>
                <LoadingButton
                  type='submit'
                  width={1}
                  loading={loading}
                  disabled={!isValid}
                  icon='arrow-right'
                >
                  Log in
                </LoadingButton>
                {!!locale && (
                  <Loc
                    fontSize={1}
                    color='gray700'
                    fontWeight='400'
                    copy='Log in'
                  />
                )}
              </Box>
              <Box mt={3} display='flex' alignItems='center'>
                <Link
                  ml='auto'
                  href='/users/password/new'
                  color='black'
                  fontSize={0}
                >
                  Forgot password?
                </Link>
              </Box>
            </Form>
          </Box>
        )
      }}
    </Formik>
  )
}
